import { PhoneIcon } from 'assets/icons';
import Button from 'components/button';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import FieldLabel from 'components/styledInput/FieldLabel';
import StyledDateInput from 'components/styledInput/StyledDateInput';
import StyledInput from 'components/styledInput/StyledInput';
import ChoiceChips from 'components/tasks/ChoiceChips';
import TaskSuggestions from 'components/tasks/suggestions/TaskSuggestions';
import { fr } from 'date-fns/locale';
import { addDays, fromIsoDay, isoDay, parseClock } from 'helper/timeLabels';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import { useNotifyRecordChanged } from 'hooks/useRecordChanged';
import useText from 'hooks/useText';
import { useEffect, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

registerLocale('fr-FR', fr);

const CALL_RESULTS = ['answered', 'callback', 'no_answer', 'not_interested'];
const DURATIONS = [30, 45, 60, 90, 120];
const LOST_REASONS = ['price', 'competitor', 'noNeed', 'direct', 'unreachable', 'closed'];
// Results after which no follow-up task is worth proposing.
const NO_SUGGESTION_RESULTS = ['no_answer', 'not_interested'];
const isMail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

// What a form holds when it opens: the contact it was opened for (or the
// first one), tomorrow at ten for an appointment, a follow-up in three months.
const initialForm = (data, now = new Date()) => {
  const contacts = data.contacts ?? [];
  const contact = contacts.find((one) => one.name === data.contactName) ?? contacts[0] ?? null;
  return {
    body: '',
    contactName: contact?.name ?? '',
    result: 'answered',
    note: '',
    address: contact?.email ?? '',
    subject: '',
    title: '',
    day: isoDay(addDays(now, 1)),
    time: '10:00',
    duration: 60,
    location: '',
    description: '',
    reason: 'price',
    details: '',
    relaunch: isoDay(addDays(now, 90)),
    noRelaunch: false,
    firstname: data.contact?.firstname ?? '',
    lastname: data.contact?.lastname ?? '',
    position: data.contact?.position ?? '',
    phone: data.contact?.phone ?? '',
    email: data.contact?.email ?? '',
    search: '',
  };
};

/**
 * The forms of a record page, in the slide-over (`objID`: { kind, companyId
 * | groupId, recordName, contacts, contactName, contact }): a note, a call
 * made, an e-mail, an appointment, a company lost, a contact of a group, a
 * company joining a group. Each writes through server/crm/crmApi and tells
 * the page (useRecordChanged).
 */
const RecordFormPanel = () => {
  const { objID } = UseContext();
  const data = objID && typeof objID === 'object' && objID.kind ? objID : null;
  const { text } = useText('recordForm');
  const { call } = useCrmContext();
  const { toggleSlideOver } = useSlideOver();
  const notify = useNotifyRecordChanged();
  const [busy, setBusy] = useState(false);
  const contacts = data?.contacts ?? [];
  const only = contacts.length === 1 ? contacts[0] : null;
  const target = data?.groupId ? { groupId: data.groupId } : { companyId: data?.companyId };

  const [form, setForm] = useState({});
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  // A new form at every opening.
  useEffect(() => {
    if (objID && typeof objID === 'object' && objID.kind) setForm(initialForm(objID));
  }, [objID]);

  // The companies that may join the group, as the search narrows them.
  const [candidates, setCandidates] = useState(null);
  useEffect(() => {
    if (data?.kind !== 'groupCompany') return undefined;
    const timer = setTimeout(
      () => call(crmApi.listGroupCandidates, { search: form.search ?? '' }).then(setCandidates, () => setCandidates([])),
      250,
    );
    return () => clearTimeout(timer);
  }, [call, data?.kind, form.search]);

  if (!data) return null;
  const close = () => toggleSlideOver();
  const submit = async (run, message) => {
    setBusy(true);
    try {
      await run();
      toast.success(message);
      notify();
      close();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };
  const contactOptions = contacts.map((contact) => ({ value: contact.name, label: contact.name }));
  const contact = contacts.find((one) => one.name === form.contactName);
  const noteContact = contacts.find((one) => one.name === data.contactName);
  const suggestionTarget = data.groupId ? { groupId: data.groupId } : { companyId: data.companyId };
  const clock = parseClock(form.time ?? '');
  const startsAt = () => {
    const day = fromIsoDay(form.day);
    day.setHours(clock[0], clock[1], 0, 0);
    return day.toISOString();
  };

  const FORMS = {
    note: {
      title: text('noteTitle'),
      cta: text('noteCta'),
      valid: Boolean(form.body?.trim()),
      save: () => submit(() => call(crmApi.addRecordNote, { ...target, body: form.body, contactName: data.contactName ?? '' }), text('noteSaved')),
      body: (
        <>
          <StyledInput
            type="textarea"
            label={text('noteLabel')}
            placeholder={text('notePlaceholder')}
            value={form.body ?? ''}
            onChange={(event) => set('body', event.target.value)}
          />
          {data.outcome && (
            <TaskSuggestions
              note={form.body ?? ''}
              origin="rendez-vous"
              {...suggestionTarget}
              contactName={data.contactName || undefined}
              contactRole={noteContact?.role}
              contactPhone={noteContact?.phone}
              contactEmail={noteContact?.email}
              enabled
            />
          )}
        </>
      ),
    },
    call: {
      title: text('callTitle', { name: only?.name ?? data.recordName }),
      hint: only ? text('callHintOne', { company: data.recordName }) : text('callHintMany'),
      cta: text('callCta'),
      valid: true,
      save: () =>
        submit(() => call(crmApi.logRecordCall, { ...target, contactName: form.contactName, result: form.result, note: form.note }), text('callSaved')),
      body: (
        <>
          {contacts.length > 1 && (
            <StyledInput type="select" label={text('who')} list={contactOptions} value={form.contactName} onChange={(event) => set('contactName', event.target.value)} />
          )}
          {contact?.phone && (
            <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-sm font-medium text-gray-900 hover:underline">
              <PhoneIcon wh={16} color="currentColor" />
              {contact.phone}
            </a>
          )}
          <div className="flex flex-col gap-2">
            <FieldLabel label={text('resultLabel')} />
            <ChoiceChips options={CALL_RESULTS.map((result) => [result, text(`result_${result}`)])} value={form.result} onPick={(result) => set('result', result)} />
          </div>
          <StyledInput type="textarea" label={text('callNote')} placeholder={text('callNotePlaceholder')} value={form.note ?? ''} onChange={(event) => set('note', event.target.value)} />
          <TaskSuggestions
            note={form.note ?? ''}
            origin="appel"
            {...suggestionTarget}
            contactName={contact?.name}
            contactRole={contact?.role}
            contactPhone={contact?.phone}
            contactEmail={contact?.email}
            enabled={!NO_SUGGESTION_RESULTS.includes(form.result)}
          />
        </>
      ),
    },
    email: {
      title: text('emailTitle'),
      cta: text('emailCta'),
      valid: isMail(form.address ?? '') && Boolean(form.subject?.trim()) && Boolean(form.body?.trim()),
      save: () =>
        submit(
          () => call(crmApi.sendRecordEmail, { ...target, contactName: form.contactName, address: form.address, subject: form.subject, body: form.body }),
          text('emailSaved'),
        ),
      body: (
        <>
          {contacts.length > 1 && (
            <StyledInput
              type="select"
              label={text('to')}
              list={contactOptions}
              value={form.contactName}
              onChange={(event) => {
                const picked = contacts.find((one) => one.name === event.target.value);
                setForm((current) => ({ ...current, contactName: event.target.value, address: picked?.email ?? current.address }));
              }}
            />
          )}
          <StyledInput type="email" label={text('address')} value={form.address ?? ''} onChange={(event) => set('address', event.target.value)} />
          <StyledInput type="text" label={text('subject')} value={form.subject ?? ''} onChange={(event) => set('subject', event.target.value)} />
          <StyledInput type="textarea" label={text('message')} value={form.body ?? ''} onChange={(event) => set('body', event.target.value)} />
          <p className="text-xs text-gray-500">{text('emailHint')}</p>
        </>
      ),
    },
    event: {
      title: text('eventTitle'),
      cta: text('eventCta'),
      valid: Boolean(clock) && Boolean(form.day),
      save: () =>
        submit(
          () =>
            call(crmApi.createRecordEvent, {
              ...target,
              title: form.title,
              startsAt: startsAt(),
              durationMin: Number(form.duration),
              location: form.location,
              mode: form.location ? text('onSite') : '',
              contactName: form.contactName,
              description: form.description,
            }),
          text('eventSaved'),
        ),
      body: (
        <>
          <StyledInput type="text" label={text('eventWhat')} placeholder={text('eventWhatPlaceholder')} value={form.title ?? ''} onChange={(event) => set('title', event.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <DatePicker
              dateFormat="dd/MM/yyyy"
              selected={form.day ? fromIsoDay(form.day) : null}
              onChange={(date) => date && set('day', isoDay(date))}
              locale="fr-FR"
              popperPlacement="bottom"
              customInput={<StyledDateInput label={text('day')} />}
            />
            <StyledInput type="text" label={text('time')} value={form.time ?? ''} error={clock ? null : text('timeError')} onChange={(event) => set('time', event.target.value)} />
          </div>
          <div className="flex flex-col gap-2">
            <FieldLabel label={text('duration')} />
            <ChoiceChips options={DURATIONS.map((minutes) => [minutes, text(`duration_${minutes}`)])} value={Number(form.duration)} onPick={(minutes) => set('duration', minutes)} />
          </div>
          <StyledInput type="text" label={text('location')} placeholder={text('locationPlaceholder')} value={form.location ?? ''} onChange={(event) => set('location', event.target.value)} />
          {contacts.length > 0 && (
            <StyledInput type="select" label={text('with')} list={contactOptions} value={form.contactName} onChange={(event) => set('contactName', event.target.value)} />
          )}
          <StyledInput type="textarea" label={text('description')} value={form.description ?? ''} onChange={(event) => set('description', event.target.value)} />
        </>
      ),
    },
    lost: {
      title: text('lostTitle'),
      cta: text('lostCta'),
      valid: true,
      save: () =>
        submit(
          () =>
            call(crmApi.setCompanyStage, {
              companyId: data.companyId,
              stage: 'lost',
              lostReason: [text(`reason_${form.reason}`), form.details?.trim()].filter(Boolean).join(' · '),
              relaunchOn: form.noRelaunch ? null : form.relaunch,
            }),
          text('lostSaved'),
        ),
      body: (
        <>
          <StyledInput
            type="select"
            label={text('reason')}
            list={LOST_REASONS.map((reason) => ({ value: reason, label: text(`reason_${reason}`) }))}
            value={form.reason}
            onChange={(event) => set('reason', event.target.value)}
          />
          <StyledInput type="textarea" label={text('reasonDetails')} value={form.details ?? ''} onChange={(event) => set('details', event.target.value)} />
          {!form.noRelaunch && (
            <DatePicker
              dateFormat="dd/MM/yyyy"
              selected={form.relaunch ? fromIsoDay(form.relaunch) : null}
              onChange={(date) => date && set('relaunch', isoDay(date))}
              locale="fr-FR"
              popperPlacement="bottom"
              customInput={<StyledDateInput label={text('relaunch')} />}
            />
          )}
          <StyledInput type="checkbox" label={text('noRelaunch')} checked={Boolean(form.noRelaunch)} onChange={() => set('noRelaunch', !form.noRelaunch)} />
        </>
      ),
    },
    groupContact: {
      title: text(data.contact ? 'contactEditTitle' : 'contactAddTitle'),
      cta: text('contactCta'),
      valid: Boolean(form.lastname?.trim()) && (!form.email?.trim() || isMail(form.email)),
      save: () =>
        submit(
          () =>
            call(crmApi.saveGroupContact, {
              groupId: data.groupId,
              contact: {
                ...data.contact,
                firstname: form.firstname.trim(),
                lastname: form.lastname.trim().toUpperCase(),
                position: form.position.trim(),
                phone: form.phone.trim(),
                email: form.email.trim(),
              },
            }),
          text('contactSaved'),
        ),
      body: (
        <div className="grid grid-cols-2 gap-3">
          <StyledInput type="text" label={text('firstname')} value={form.firstname ?? ''} onChange={(event) => set('firstname', event.target.value)} />
          <StyledInput type="text" label={text('lastname')} value={form.lastname ?? ''} onChange={(event) => set('lastname', event.target.value)} />
          <StyledInput type="text" label={text('position')} value={form.position ?? ''} onChange={(event) => set('position', event.target.value)} />
          <StyledInput type="text" label={text('phone')} value={form.phone ?? ''} onChange={(event) => set('phone', event.target.value)} />
          <div className="col-span-2">
            <StyledInput type="email" label={text('email')} value={form.email ?? ''} onChange={(event) => set('email', event.target.value)} />
          </div>
        </div>
      ),
    },
    groupCompany: {
      title: text('groupCompanyTitle'),
      body: (
        <>
          <StyledInput type="text" label={text('searchCompany')} value={form.search ?? ''} onChange={(event) => set('search', event.target.value)} />
          <div className="flex flex-col">
            {(candidates ?? []).map((company) => (
              <div key={company.id} className="flex items-center gap-3 border-b border-gray-100 py-2.5 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium uppercase text-gray-900">{company.name}</p>
                  <p className="truncate text-xs text-gray-600">{company.department}</p>
                </div>
                <Button
                  color="SECONDARY"
                  size="SM"
                  disabled={busy}
                  onClick={() =>
                    submit(
                      () => call(crmApi.addCompanyToGroup, { companyId: company.id, groupId: data.groupId }),
                      text('groupCompanySaved', { company: company.name, group: data.recordName }),
                    )
                  }
                >
                  {text('groupCompanyCta')}
                </Button>
              </div>
            ))}
            {candidates && !candidates.length && <p className="py-4 text-sm text-gray-600">{text('groupCompanyNone')}</p>}
          </div>
        </>
      ),
    },
  };
  const current = FORMS[data.kind];
  if (!current) return null;

  return (
    <>
      <SlideOverHeader hide={close} closeLabel={text('close')}>
        <span className="flex flex-col">
          {current.title}
          <span className="text-sm font-normal text-gray-600">{current.hint ?? data.recordName}</span>
        </span>
      </SlideOverHeader>
      <SlideOverBody>
        <div className="flex flex-col gap-4">{current.body}</div>
      </SlideOverBody>
      {current.save && (
        <div className="flex-shrink-0 border-t border-gray-100">
          <SlideOverFooter>
            <Button color="SECONDARY" size="SM" onClick={close}>
              {text('cancel')}
            </Button>
            <Button color="PRIMARY" size="SM" disabled={!current.valid || busy} onClick={current.save}>
              {current.cta}
            </Button>
          </SlideOverFooter>
        </div>
      )}
    </>
  );
};

export default RecordFormPanel;
